import React, { useState, useEffect } from 'react';
import { Card, Input, InputNumber, Button, Spin, message, Space, Upload, Divider, Row, Col, Typography, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined, SaveOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const { Text } = Typography;

const EMPTY_LINK = { id: '', icon: '', image: '', imageWidth: '', imageHeight: '', labelFa: '', labelEn: '', href: '' };

function newId() {
  return `link-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

// ── لیست لینک‌های یک بخش (هدر یا فوتر) ───────────────────────────────────────
// هر آیتم فقط یک تصویر/آیکون دارد که بین نسخه فارسی و انگلیسی سایت مشترک است؛
// فقط برچسب متنی (labelFa/labelEn) به تفکیک زبان جداست.
function LinkListEditor({ title, links, onChange }) {
  const [uploadingIdx, setUploadingIdx] = useState(null);

  const update = (i, patch) => {
    const next = links.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(links.filter((_, idx) => idx !== i));
  const add = () => onChange([...links, { ...EMPTY_LINK, id: newId() }]);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= links.length) return;
    const next = links.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const handleUpload = async (i, file) => {
    setUploadingIdx(i);
    try {
      const res = await adminAPI.uploadImage(file);
      if (res.data.success) {
        update(i, { image: res.data.path });
        message.success('آیکون آپلود شد');
      }
    } catch (err) {
      message.error(err.response?.data?.message || err.message || 'خطا در آپلود آیکون');
    } finally {
      setUploadingIdx(null);
    }
    return false; // جلوگیری از آپلود خودکار antd
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <Text strong style={{ fontSize: 16 }}>{title}</Text>
      <div style={{ marginTop: 12 }}>
        {links.map((link, i) => (
          <Card
            size="small"
            key={link.id || i}
            style={{ marginBottom: 12 }}
            title={link.labelFa || link.labelEn || `لینک ${i + 1}`}
            extra={
              <Space size={4}>
                <Button size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => move(i, -1)} />
                <Button size="small" icon={<ArrowDownOutlined />} disabled={i === links.length - 1} onClick={() => move(i, 1)} />
                <Button danger size="small" icon={<DeleteOutlined />} onClick={() => remove(i)} />
              </Space>
            }
          >
            <Row gutter={12}>
              <Col xs={24} md={8}>
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>آیکون (تصویر آپلودی یا اموجی — بین فارسی/انگلیسی مشترک است)</Text>
                </div>
                <Space align="start">
                  {link.image && (
                    <img
                      src={link.image}
                      alt=""
                      style={{
                        width: link.imageWidth || 40,
                        height: link.imageHeight || 40,
                        maxWidth: 80,
                        maxHeight: 80,
                        objectFit: 'contain',
                        borderRadius: 8,
                        border: '1px solid #d9d9d9'
                      }}
                      onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
                    />
                  )}
                  <Space direction="vertical" size={4}>
                    <Upload beforeUpload={(file) => handleUpload(i, file)} showUploadList={false} accept="image/*">
                      <Button size="small" icon={<UploadOutlined />} loading={uploadingIdx === i}>
                        {link.image ? 'تغییر آیکون' : 'آپلود آیکون'}
                      </Button>
                    </Upload>
                    {link.image && (
                      <Button size="small" danger type="text" onClick={() => update(i, { image: '' })}>حذف تصویر</Button>
                    )}
                    {link.image && (
                      <Space size={4}>
                        <InputNumber
                          size="small"
                          min={0}
                          value={link.imageWidth || undefined}
                          onChange={(v) => update(i, { imageWidth: v || '' })}
                          placeholder="عرض (px)"
                          style={{ width: 90 }}
                        />
                        <InputNumber
                          size="small"
                          min={0}
                          value={link.imageHeight || undefined}
                          onChange={(v) => update(i, { imageHeight: v || '' })}
                          placeholder="ارتفاع (px)"
                          style={{ width: 90 }}
                        />
                      </Space>
                    )}
                    <Input
                      size="small"
                      value={link.icon}
                      onChange={(e) => update(i, { icon: e.target.value })}
                      placeholder="اموجی (اگر تصویری نباشد)"
                      style={{ width: 180 }}
                    />
                  </Space>
                </Space>
              </Col>
              <Col xs={24} md={16}>
                <Input
                  style={{ marginBottom: 8 }}
                  value={link.labelFa}
                  onChange={(e) => update(i, { labelFa: e.target.value })}
                  placeholder="برچسب فارسی"
                />
                <Input
                  style={{ marginBottom: 8 }}
                  value={link.labelEn}
                  onChange={(e) => update(i, { labelEn: e.target.value })}
                  placeholder="برچسب انگلیسی"
                />
                <Input
                  value={link.href}
                  onChange={(e) => update(i, { href: e.target.value })}
                  placeholder="لینک مقصد، مثلاً /about"
                />
              </Col>
            </Row>
          </Card>
        ))}
        <Button size="small" icon={<PlusOutlined />} onClick={add}>افزودن لینک</Button>
      </div>
    </div>
  );
}

export default function SiteNavManager() {
  const [nav, setNav] = useState({ header: [], footer: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getSiteNav();
      if (res.data.success) {
        const value = res.data.config?.value || {};
        setNav({
          header: Array.isArray(value.header) ? value.header : [],
          footer: Array.isArray(value.footer) ? value.footer : []
        });
      }
    } catch (err) {
      message.error('خطا در بارگذاری لینک‌های منو');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminAPI.upsertSiteNav(nav);
      if (res.data.success) {
        message.success('لینک‌های منو ذخیره شد');
      }
    } catch (err) {
      message.error('خطا در ذخیره لینک‌های منو');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  return (
    <div>
      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="لینک‌های هدر و فوتر سایت"
        description="آیتم‌های منوی بالای سایت و فوتر را اینجا اضافه، حذف یا جابه‌جا کنید. هر آیتم یک آیکون/تصویر دارد که هم‌زمان برای نسخه فارسی و انگلیسی سایت استفاده می‌شود؛ فقط متن برچسب به تفکیک زبان است."
      />
      <LinkListEditor title="منوی هدر" links={nav.header} onChange={(header) => setNav((prev) => ({ ...prev, header }))} />
      <Divider />
      <LinkListEditor title="لینک‌های فوتر" links={nav.footer} onChange={(footer) => setNav((prev) => ({ ...prev, footer }))} />
      <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
        ذخیره تغییرات
      </Button>
    </div>
  );
}
